import listingPlaceholderSquare from '../../assets/listing_placeholder_square.png';
import listingPlaceholderTall from '../../assets/listing_placeholder_tall.png';
import listingPlaceholderWide from '../../assets/listing_placeholder_wide.png';
import { cn, getAspectRatioClass, getTierClasses } from './listingCardUtils.js';

function getChipMediaClasses(tier = 'basic') {
  return {
    basic: {
      shell: 'border-slate-200 bg-slate-100',
      initials: 'text-[color:var(--listing-card-text,#334155)]',
    },
    featured: {
      shell: 'border-[color:var(--listing-card-primary,#2563EB)] bg-white/80',
      initials: 'text-[color:var(--listing-card-primary,#2563EB)]',
    },
    spotlight: {
      shell: 'border-[color:var(--listing-card-accent,#F59E0B)] bg-white/80',
      initials: 'text-[color:var(--listing-card-accent,#F59E0B)]',
    },
  }[tier] || {
    shell: 'border-slate-200 bg-slate-100',
    initials: 'text-[color:var(--listing-card-text,#334155)]',
  };
}

function getPlaceholderImage(aspectRatio = 'square') {
  if (aspectRatio === 'wide') return listingPlaceholderWide;
  if (aspectRatio === 'portrait') return listingPlaceholderTall;
  return listingPlaceholderSquare;
}

export default function ListingCardMedia({
  listing,
  media = {},
  variant = 'content',
  tier = 'basic',
  show = {},
}) {
  const mode = media.mode || 'logo';
  if (mode === 'none') return null;
  if (mode === 'image' && show.image === false) return null;
  if (mode === 'logo' && show.logo === false) return null;

  const tierClasses = getTierClasses(tier);
  const coverMediaVariant = variant === 'content' || variant === 'directory';
  const imageUrl = coverMediaVariant ? listing.imageUrl : listing.imageUrl || listing.logoUrl;
  const placeholderImageUrl = mode === 'image' || mode === 'background' ? getPlaceholderImage(media.aspectRatio) : '';
  const resolvedImageUrl = imageUrl || placeholderImageUrl;
  const logoUrl = listing.logoUrl || listing.imageUrl;
  const canUseImage = Boolean(mode === 'image' || mode === 'background') && Boolean(resolvedImageUrl);
  const canUseLogo = mode === 'logo' && Boolean(logoUrl);
  const compact = variant === 'compact' || variant === 'chip' || variant === 'row' || variant === 'directoryRow';
  const chipMediaClasses = variant === 'chip' ? getChipMediaClasses(tier) : null;

  if (variant === 'hero' || mode === 'background') {
    return (
      <div
        className={cn('absolute inset-0 bg-slate-900', canUseImage ? 'bg-cover bg-center' : '')}
        style={canUseImage ? { backgroundImage: `url(${resolvedImageUrl})` } : undefined}
        aria-hidden="true"
      >
        {media.overlay ? <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" /> : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden rounded-lg border',
        variant === 'content' ? 'rounded-none border-0 bg-slate-100' : '',
        variant === 'directory' ? 'h-[170px] rounded-none border-0 bg-slate-100' : '',
        variant === 'chip' ? chipMediaClasses.shell : cn('bg-slate-100', tierClasses.border),
        variant === 'row' ? 'h-10 w-10 rounded-full' : '',
        compact && variant !== 'row' ? 'h-14 w-14' : '',
        !compact ? 'w-full' : '',
        !compact && variant !== 'directory' ? getAspectRatioClass(media.aspectRatio) : '',
      )}
    >
      {canUseImage || canUseLogo ? (
        <img
          src={mode === 'logo' ? logoUrl : resolvedImageUrl}
          alt=""
          className={cn(
            'h-full w-full',
            mode === 'logo' ? 'object-contain p-2' : 'object-cover',
          )}
        />
      ) : (
        <div className={cn(
          'flex h-full w-full items-center justify-center text-sm font-bold',
          coverMediaVariant ? 'bg-gradient-to-br from-slate-50 to-slate-100 text-slate-400' : '',
          variant === 'chip' ? chipMediaClasses.initials : tierClasses.accent,
        )}>
          {coverMediaVariant ? listing.initials : listing.initials}
        </div>
      )}
    </div>
  );
}
